<template>
  <Transition name="fade">
    <Dialog
      title="Logo inspector"
      :show-close-button="true"
      :show-footer="true"
      :custom-footer="true"
      :loading="loading"
      lg
      @closeEvent="emit('close')"
    >
      <template #modal-content>
        <div v-if="!entries.length" class="py-10 text-center text-sm text-ink-mute">
          This product has no custom logos.
        </div>

        <div v-else class="flex gap-4 min-h-[460px]">
          <!-- Logo list -->
          <aside class="w-60 shrink-0 space-y-2">
            <button
              v-for="entry in entries"
              :key="entry.index"
              type="button"
              class="w-full flex items-center gap-3 rounded-lg border p-2 text-left transition-colors"
              :class="
                entry.index === selectedIndex
                  ? 'border-primary bg-primary/5'
                  : 'border-[#e5e5e5] hover:border-[#bbbbbb]'
              "
              @click="selectedIndex = entry.index"
            >
              <span
                class="checker w-12 h-12 shrink-0 rounded border border-line overflow-hidden"
              >
                <img
                  v-if="isImageExt(entry.originalExt)"
                  :src="entry.originalUrl"
                  :alt="entry.name"
                  class="w-full h-full object-contain"
                />
                <span v-else class="ext-badge">{{ entry.originalExt.toUpperCase() || '?' }}</span>
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-medium truncate">{{ entry.name }}</span>
                <span class="block text-xs text-ink-mute truncate">
                  {{ entry.placement }} · {{ sourceLabel(choices[entry.index] ?? 'auto') }}
                </span>
                <span
                  v-if="isDirty(entry)"
                  class="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600"
                >
                  unsaved
                </span>
              </span>
            </button>
          </aside>

          <!-- Compare -->
          <section v-if="current" class="flex-1 min-w-0 flex flex-col gap-3">
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <div
                class="inline-flex rounded-full border border-line overflow-hidden text-sm"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  :aria-selected="mode === 'side'"
                  class="px-4 py-1.5"
                  :class="mode === 'side' ? 'bg-[#1d2f5f] text-white' : 'bg-surface text-ink-soft'"
                  @click="switchMode('side')"
                >
                  Side by side
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="mode === 'lens'"
                  class="px-4 py-1.5"
                  :class="mode === 'lens' ? 'bg-[#1d2f5f] text-white' : 'bg-surface text-ink-soft'"
                  @click="switchMode('lens')"
                >
                  Overlay
                </button>
              </div>
              <p class="text-xs text-ink-mute">
                <template v-if="mode === 'side'"
                  >Scroll to zoom (both views stay in sync), drag to pan, double-click to
                  reset.</template
                >
                <template v-else
                  >Move the cursor — the circle reveals the chosen version on top of the original.
                  Scroll to zoom, drag to pan, double-click to reset.</template
                >
              </p>
            </div>

            <!-- Side by side -->
            <div v-if="mode === 'side'" class="grid grid-cols-2 gap-3">
              <div
                class="pane checker"
                @wheel.prevent="onWheel"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
                @pointercancel="onPointerUp"
                @pointerleave="ringVisible = false"
                @dblclick="resetZoom"
                :style="{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'crosshair' }"
              >
                <div class="zoomable" :style="zoomStyle">
                  <AssetView
                    :url="current.originalUrl"
                    :ext="current.originalExt"
                    label="Original logo"
                  />
                </div>
                <span class="layer-tag">Original ({{ current.originalExt.toUpperCase() }})</span>
                <div v-show="ringVisible" class="cursor-ring" :style="ringStyle"></div>
              </div>
              <div
                class="pane checker"
                @wheel.prevent="onWheel"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
                @pointercancel="onPointerUp"
                @pointerleave="ringVisible = false"
                @dblclick="resetZoom"
                :style="{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'crosshair' }"
              >
                <div class="zoomable" :style="zoomStyle">
                  <AssetView
                    v-if="compareAsset"
                    :url="compareAsset.url"
                    :ext="compareAsset.ext"
                    :label="compareAsset.label"
                  />
                  <EmptyCompare
                    v-else
                    :message="compareMissingText"
                    :choice="choices[current.index] ?? 'auto'"
                    :busy="busy"
                    :can-vectorize="
                      !autoAsset(current) && infos[current.index]?.status !== 'inProcess'
                    "
                    :can-upload="!!current.manualBase"
                    @vectorize="vectorizeNow(current)"
                    @upload="onManualFile(current, $event)"
                  />
                </div>
                <span class="layer-tag" :class="compareTagClass">{{
                  compareAsset?.label ?? 'Nothing to compare'
                }}</span>
                <div v-show="ringVisible" class="cursor-ring" :style="ringStyle"></div>
              </div>
            </div>

            <!-- Overlay lens -->
            <div
              v-else
              class="compare checker"
              :style="{
                ...lensVars,
                cursor: zoom > 1 ? (dragging ? 'grabbing' : 'crosshair') : 'crosshair',
              }"
              @wheel.prevent="onWheel"
              @pointerdown="onPointerDown"
              @pointermove="onLensMove"
              @pointerup="onPointerUp"
              @pointercancel="onPointerUp"
              @dblclick="resetZoom"
            >
              <div class="zoomable" :style="zoomStyle">
                <AssetView
                  :url="current.originalUrl"
                  :ext="current.originalExt"
                  label="Original logo"
                />
              </div>
              <div class="vector-clip">
                <div class="zoomable" :style="zoomStyle">
                  <AssetView
                    v-if="compareAsset"
                    :url="compareAsset.url"
                    :ext="compareAsset.ext"
                    :label="compareAsset.label"
                  />
                  <EmptyCompare
                    v-else
                    :message="compareMissingText"
                    :choice="choices[current.index] ?? 'auto'"
                    :busy="busy"
                    :can-vectorize="
                      !autoAsset(current) && infos[current.index]?.status !== 'inProcess'
                    "
                    :can-upload="!!current.manualBase"
                    @vectorize="vectorizeNow(current)"
                    @upload="onManualFile(current, $event)"
                  />
                </div>
              </div>
              <span class="layer-tag tag-original"
                >Original ({{ current.originalExt.toUpperCase() }})</span
              >
              <span class="layer-tag tag-vectorized" :class="compareTagClass">{{
                compareAsset?.label ?? 'Nothing to compare'
              }}</span>
              <div class="lens-ring"></div>
            </div>

            <!-- Source choice -->
            <fieldset class="grid grid-cols-3 gap-3">
              <legend class="sr-only">Production source</legend>
              <label
                v-for="option in sourceOptions"
                :key="option.value"
                class="rounded-lg border p-3 flex flex-col gap-1.5 cursor-pointer"
                :class="[
                  choices[current.index] === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-[#e5e5e5]',
                  !option.available && choices[current.index] !== option.value ? 'opacity-70' : '',
                ]"
                @click="choose(current.index, option.value)"
              >
                <span class="flex items-center gap-2">
                  <input
                    type="radio"
                    :name="`logo-source-${current.index}`"
                    :value="option.value"
                    :checked="choices[current.index] === option.value"
                    @change="choose(current.index, option.value)"
                  />
                  <span class="text-sm font-semibold">{{ option.title }}</span>
                  <span
                    v-if="option.badge"
                    class="ml-auto text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                    :class="option.badgeClass"
                    >{{ option.badge }}</span
                  >
                </span>
                <span class="text-xs text-ink-soft leading-snug">{{ option.description }}</span>

                <template v-if="option.value === 'auto' && !option.available">
                  <button
                    type="button"
                    class="mt-auto self-start text-xs font-medium text-primary underline disabled:opacity-50"
                    :disabled="busy !== null"
                    @click.prevent="vectorizeNow(current)"
                  >
                    {{ busy === 'vectorize' ? 'Vectorizing…' : 'Vectorize now' }}
                  </button>
                </template>
                <template v-if="option.value === 'manual'">
                  <label
                    class="mt-auto self-start text-xs font-medium text-primary underline cursor-pointer"
                  >
                    {{
                      busy === 'upload'
                        ? 'Uploading…'
                        : option.available
                          ? 'Replace manual vector'
                          : 'Upload manual vector'
                    }}
                    <input
                      type="file"
                      class="hidden"
                      accept=".svg,.pdf,.eps,.ai"
                      :disabled="busy !== null"
                      @change="onManualFile(current, $event)"
                    />
                  </label>
                </template>
              </label>
            </fieldset>
          </section>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <p class="text-xs text-ink-mute">
            <template v-if="blockedCount"
              >{{ blockedCount }} logo{{ blockedCount === 1 ? '' : 's' }} point{{
                blockedCount === 1 ? 's' : ''
              }}
              at a source that does not exist yet — vectorize or upload first, or pick another
              source.</template
            >
            <template v-else-if="dirtyCount"
              >{{ dirtyCount }} logo{{ dirtyCount === 1 ? '' : 's' }} with an unsaved
              choice.</template
            >
            <template v-else
              >Choices are stored on the order and used by every production download.</template
            >
          </p>
          <div class="flex gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-lg border border-line text-sm"
              @click="emit('close')"
            >
              Close
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
              :disabled="!dirtyCount || !!blockedCount || saving"
              @click="save"
            >
              {{ saving ? 'Saving…' : 'Save for production' }}
            </button>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>

<script setup lang="ts">
/**
 * Logo inspector (AB#20790): for every custom logo on the order line, compare
 * the upload with its vectorized versions — the trace made at order placement,
 * a designer's manual vector — and choose which one production files place.
 * The comparison UI mirrors the customer "Download vectorized" landing page
 * (side by side / overlay lens, synced zoom). Choices are saved on the order
 * (`vectorized_logo_info.production_source`); no vectorizer call is made
 * unless staff ask for one on a logo that has no trace.
 */
import {
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  type PropType,
} from 'vue';
import { messageForError } from '@/utils/userMessage';
import {
  IDENTITY_VIEW,
  zoomAt,
  zoomBoxStyle,
  type ZoomView,
} from '@/utils/productionFile/compareZoom';
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import type { FactoryProduct } from '@/models';
import type { CustomLogo, LogoVectorSource, VectorizedLogoInfo } from '@/models/CustomLogo';
import { productionStorageUrl } from '@/utils/productionFile/endpoints';
import {
  autoVectorKey,
  effectiveLogoSource,
  fileExtension,
  manualVectorBaseKey,
  manualVectorKey,
  originalLogoKey,
  placedLogoKey,
  storedLogoSource,
  type ManualVectorInfo,
} from '@/utils/productionFile/logoSources';
import { isLogoVisibleForProduct } from '@/utils/productionFile/logoScope';
import {
  fetchManualVectorInfo,
  requestAutoVectorization,
  saveLogoVectorSource,
  uploadManualVector,
} from '@/composables/useLogoSources';

const props = defineProps<{
  product: FactoryProduct;
  orderItemId: number | string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
}>();

const IMAGE_EXTS = ['svg', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'];
function isImageExt(ext: string): boolean {
  return IMAGE_EXTS.includes(ext.toLowerCase());
}

/** One asset pane: image, PDF embed, or a badge for formats a browser cannot draw. */
const AssetView = defineComponent({
  props: {
    url: { type: String, required: true },
    ext: { type: String, required: true },
    label: { type: String, required: true },
  },
  setup(p) {
    // A fresh trace is a megabyte of SVG: show that it is loading (or failed)
    // instead of an empty pane that reads as "nothing happened".
    const state = ref<'loading' | 'ready' | 'error'>('loading');
    watch(
      () => p.url,
      () => {
        state.value = 'loading';
      }
    );
    return () => {
      const ext = p.ext.toLowerCase();
      if (isImageExt(ext)) {
        return h('div', { class: 'viewer-wrap' }, [
          h('img', {
            class: ['viewer-img', state.value !== 'ready' ? 'viewer-img--pending' : ''],
            src: p.url,
            alt: p.label,
            draggable: false,
            onLoad: () => {
              state.value = 'ready';
            },
            onError: () => {
              state.value = 'error';
            },
          }),
          state.value === 'loading'
            ? h('div', { class: 'pane-message pane-message--overlay' }, 'Loading preview…')
            : null,
          state.value === 'error'
            ? h('div', { class: 'pane-message pane-message--overlay' }, [
                h('span', {}, 'The file could not be loaded from storage.'),
                h('span', { class: 'text-[10px] break-all' }, p.url),
              ])
            : null,
        ]);
      }
      if (ext === 'pdf') {
        return h('embed', { class: 'viewer-embed', src: p.url, type: 'application/pdf' });
      }
      return h('div', { class: 'pane-message' }, [
        h('span', { class: 'ext-badge' }, ext.toUpperCase() || '?'),
        h('span', {}, 'Cannot be previewed in the browser'),
      ]);
    };
  },
});

/**
 * Empty compare pane: says what is missing and offers the action that fills
 * it — so a user can pick "Manual vector" first and upload from the preview,
 * or pick "AI vectorized" and trace from there.
 */
const EmptyCompare = defineComponent({
  props: {
    message: { type: String, required: true },
    choice: { type: String as PropType<LogoVectorSource>, required: true },
    busy: { type: String as PropType<'vectorize' | 'upload' | null>, default: null },
    canVectorize: { type: Boolean, default: false },
    canUpload: { type: Boolean, default: false },
  },
  emits: ['vectorize', 'upload'],
  setup(p, { emit: emitEmpty }) {
    return () => {
      const children = [h('span', {}, p.message)];
      if (p.choice === 'auto' && p.canVectorize) {
        children.push(
          h(
            'button',
            {
              type: 'button',
              class: 'cta-button',
              disabled: p.busy !== null,
              onClick: (e: Event) => {
                e.stopPropagation();
                emitEmpty('vectorize');
              },
            },
            p.busy === 'vectorize' ? 'Vectorizing…' : 'Vectorize now'
          )
        );
      }
      if (p.choice === 'manual' && p.canUpload) {
        children.push(
          h('label', { class: 'cta-button', onPointerdown: (e: Event) => e.stopPropagation() }, [
            p.busy === 'upload' ? 'Uploading…' : 'Upload manual vector',
            h('input', {
              type: 'file',
              class: 'hidden',
              accept: '.svg,.pdf,.eps,.ai',
              disabled: p.busy !== null,
              onChange: (e: Event) => emitEmpty('upload', e),
            }),
          ])
        );
      }
      return h('div', { class: 'pane-message pane-message--actions' }, children);
    };
  },
});

const storage = productionStorageUrl();
const bust = ref(Date.now());

interface Entry {
  index: number;
  logo: CustomLogo;
  name: string;
  placement: string;
  placedKey: string;
  originalKey: string;
  originalExt: string;
  originalUrl: string;
  manualBase: string | null;
}

/** Live copy of each logo's info so vectorize/upload results show immediately. */
const infos = reactive<Record<number, VectorizedLogoInfo | undefined>>({});
const manualByIndex = reactive<Record<number, ManualVectorInfo | null | undefined>>({});
const choices = reactive<Record<number, LogoVectorSource>>({});
/** Logos the user acted on (radio, upload, vectorize) — only those can be saved. */
const touched = reactive<Record<number, boolean>>({});

function choose(index: number, source: LogoVectorSource): void {
  choices[index] = source;
  touched[index] = true;
}

const entries = computed<Entry[]>(() =>
  (props.product.custom_logos ?? [])
    .map((logo, index) => ({ logo, index }))
    // Logos the builder hid on this design are not printed: nothing to inspect.
    .filter(({ logo }) => !!placedLogoKey(logo) && isLogoVisibleForProduct(logo, props.product))
    .map(({ logo, index }) => {
      const originalKey = originalLogoKey(logo) ?? placedLogoKey(logo)!;
      return {
        index,
        logo,
        name: logo.logo_name || `Logo ${index + 1}`,
        placement: logo.name_of_placement || logo.side || '',
        placedKey: placedLogoKey(logo)!,
        originalKey,
        originalExt: fileExtension(originalKey),
        originalUrl: `${storage}${originalKey}`,
        manualBase: manualVectorBaseKey(logo),
      };
    })
);

const selectedIndex = ref<number>(-1);
const current = computed(() => entries.value.find((e) => e.index === selectedIndex.value) ?? null);

const loading = ref(true);
const saving = ref(false);
const busy = ref<'vectorize' | 'upload' | null>(null);

function logoWithLiveInfo(entry: Entry): CustomLogo {
  return {
    ...entry.logo,
    vectorized_logo_info: infos[entry.index] ?? entry.logo.vectorized_logo_info,
  };
}

onMounted(async () => {
  try {
    await Promise.all(
      entries.value.map(async (entry) => {
        infos[entry.index] = entry.logo.vectorized_logo_info;
        manualByIndex[entry.index] = entry.manualBase
          ? await fetchManualVectorInfo(entry.manualBase)
          : null;
      })
    );
    entries.value.forEach((entry) => {
      choices[entry.index] = effectiveLogoSource(
        logoWithLiveInfo(entry),
        manualByIndex[entry.index]
      );
    });
    selectedIndex.value = entries.value[0]?.index ?? -1;
  } finally {
    loading.value = false;
  }
});

// ---- assets per source ----------------------------------------------------

interface Asset {
  url: string;
  ext: string;
  label: string;
}

function autoAsset(entry: Entry): Asset | null {
  const key = autoVectorKey(logoWithLiveInfo(entry));
  return key
    ? { url: `${storage}${key}?v=${bust.value}`, ext: 'svg', label: 'AI vectorized (SVG)' }
    : null;
}

function manualAsset(entry: Entry): Asset | null {
  const manual = manualByIndex[entry.index];
  if (!manual?.has_manual || !manual.extension || !entry.manualBase) return null;
  return {
    url: `${storage}${manualVectorKey(entry.manualBase, manual.extension)}?v=${bust.value}`,
    ext: manual.extension,
    label: `Manual vector (${manual.extension.toUpperCase()})`,
  };
}

function originalAsset(entry: Entry): Asset {
  return {
    url: entry.originalUrl,
    ext: entry.originalExt,
    label: `Uploaded file (${entry.originalExt.toUpperCase()})`,
  };
}

const compareAsset = computed<Asset | null>(() => {
  if (!current.value) return null;
  const choice = choices[current.value.index] ?? 'auto';
  if (choice === 'manual') return manualAsset(current.value);
  if (choice === 'original') return originalAsset(current.value);
  return autoAsset(current.value);
});

const compareMissingText = computed(() => {
  if (!current.value) return '';
  const choice = choices[current.value.index];
  if (choice === 'manual') return 'No manual vector uploaded for this logo yet.';
  const status = infos[current.value.index]?.status;
  if (status === 'inProcess') return 'The auto trace is still running — check back shortly.';
  if (status === 'failed')
    return `Auto trace failed: ${infos[current.value.index]?.message || 'unknown error'}`;
  return 'No auto trace stored for this logo yet.';
});

const compareTagClass = computed(() => {
  const choice = current.value ? choices[current.value.index] : 'auto';
  return choice === 'manual' ? 'tag-manual' : choice === 'original' ? 'tag-none' : 'tag-ai';
});

function sourceLabel(source: LogoVectorSource): string {
  return source === 'manual'
    ? 'manual vector'
    : source === 'original'
      ? 'uploaded file'
      : 'AI vectorized';
}

interface SourceOption {
  value: LogoVectorSource;
  title: string;
  description: string;
  available: boolean;
  badge?: string;
  badgeClass?: string;
}

const sourceOptions = computed<SourceOption[]>(() => {
  const entry = current.value;
  if (!entry) return [];
  const auto = autoAsset(entry);
  const manual = manualByIndex[entry.index];
  const info = infos[entry.index];
  const originalIsSvg = entry.originalExt === 'svg';
  const originalIsVector = ['svg', 'pdf', 'eps', 'ai'].includes(entry.originalExt);
  const uploadedBy = manual?.meta?.uploaded_by_name || manual?.meta?.uploaded_by_email;
  const uploadedAt = manual?.meta?.uploaded_at
    ? new Date(manual.meta.uploaded_at).toLocaleDateString()
    : '';
  return [
    {
      value: 'auto',
      title: 'AI vectorized',
      description: auto
        ? 'Traced automatically when the order was placed. Placed as vector in the production file.'
        : info?.status === 'inProcess'
          ? 'Trace in progress.'
          : 'No trace stored for this logo. Vectorizing now uses one vectorizer credit.',
      available: !!auto,
      badge: auto ? 'ready' : undefined,
      badgeClass: 'bg-[#2b4170] text-white',
    },
    {
      value: 'manual',
      title: 'Manual vector',
      description: manual?.has_manual
        ? `Uploaded by ${uploadedBy || 'a designer'}${uploadedAt ? ` on ${uploadedAt}` : ''}.${
            manual.extension?.toLowerCase() === 'svg'
              ? ' Placed as vector in the production file.'
              : ' Only SVG can be placed inside the production file — this format falls back to the AI trace.'
          }`
        : 'Redrawn by a designer when the AI result is not good enough. SVG is placed directly; PDF/EPS/AI are kept for the factory only.',
      available: !!manual?.has_manual,
      badge: manual?.has_manual ? manual.extension?.toUpperCase() : undefined,
      badgeClass: 'bg-[#0b7a74] text-white',
    },
    {
      value: 'original',
      title: 'Uploaded file',
      description: originalIsSvg
        ? 'The customer uploaded a vector already — placed as is.'
        : originalIsVector
          ? `A ${entry.originalExt.toUpperCase()} upload cannot be placed inside the SVG; the raster the builder shows is kept.`
          : 'Keep the raster exactly as the customer approved it in the builder (no vectorization).',
      available: true,
      badge: entry.originalExt.toUpperCase(),
      badgeClass: 'bg-surface-3 text-white',
    },
  ];
});

// ---- dirty tracking / save -------------------------------------------------

/**
 * Saveable when the user acted on the logo and the choice is not the one
 * already STORED on the order. Compared against the stored value, not the
 * default: after uploading a manual vector the default becomes "manual" too,
 * and the explicit choice must still be persisted.
 */
function isDirty(entry: Entry): boolean {
  if (!touched[entry.index]) return false;
  return (choices[entry.index] ?? null) !== storedLogoSource(logoWithLiveInfo(entry));
}
const dirtyCount = computed(() => entries.value.filter(isDirty).length);

/** A chosen source with nothing behind it cannot be saved (production would fall back anyway). */
function isBlocked(entry: Entry): boolean {
  if (!isDirty(entry)) return false;
  const choice = choices[entry.index];
  if (choice === 'auto') return !autoAsset(entry);
  if (choice === 'manual') return !manualAsset(entry);
  return false;
}
const blockedCount = computed(() => entries.value.filter(isBlocked).length);

async function save(): Promise<void> {
  saving.value = true;
  let failed = 0;
  for (const entry of entries.value) {
    if (!isDirty(entry)) continue;
    try {
      const info = await saveLogoVectorSource(
        {
          order_item_id: props.orderItemId,
          factory_product_id: props.product.id,
          logo_index: entry.index,
        },
        choices[entry.index]
      );
      if (info) infos[entry.index] = info;
      else {
        infos[entry.index] = {
          ...(infos[entry.index] ?? { show_preview_loader: false, show_vectorized_loader: false }),
          production_source: choices[entry.index],
        };
      }
    } catch (error) {
      failed += 1;
      showToast(messageForError(error, "The logo couldn't be processed. Try again."), 'error');
    }
  }
  saving.value = false;
  if (!failed) {
    showToast('Production sources saved. Downloads now use the chosen versions.', 'success');
    emit('saved');
    emit('close');
  }
}

async function vectorizeNow(entry: Entry): Promise<void> {
  busy.value = 'vectorize';
  try {
    const info = await requestAutoVectorization(entry.logo, {
      order_item_id: props.orderItemId,
      factory_product_id: props.product.id,
      logo_index: entry.index,
    });
    if (info) infos[entry.index] = { ...info, status: info.status || 'processed' };
    choose(entry.index, 'auto');
    bust.value = Date.now();
    if (autoAsset(entry)) {
      showToast('Logo vectorized.', 'success');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[logo-inspector] trace returned without a usable SVG path', info);
      showToast(
        "The trace finished but didn't produce an SVG the preview can show.",
        'warning'
      );
    }
  } catch (error) {
    showToast(messageForError(error, "The logo couldn't be vectorized. Try again or upload a vector file."), 'error');
  } finally {
    busy.value = null;
  }
}

async function onManualFile(entry: Entry, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || !entry.manualBase) return;
  busy.value = 'upload';
  try {
    await uploadManualVector(entry.manualBase, file);
    manualByIndex[entry.index] = await fetchManualVectorInfo(entry.manualBase);
    bust.value = Date.now();
    choose(entry.index, 'manual');
    showToast('Vector file uploaded.', 'success');
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), 'error');
  } finally {
    busy.value = null;
  }
}

// ---- compare views: mode, synced zoom/pan, lens ----------------------------

const mode = ref<'side' | 'lens'>('side');
const zoom = ref(1);
const tx = ref(0);
const ty = ref(0);
const dragging = ref(false);
let dragStart: { x: number; y: number; tx: number; ty: number } | null = null;

// The view the zoom boxes are laid out at. The live view runs ahead of it on a
// cheap transform while the wheel turns, and is committed as real layout once
// it settles — Chromium only redraws a vector trace sharply at its laid-out
// size (see compareZoom.ts).
const committed = ref<ZoomView>({ ...IDENTITY_VIEW });
const SETTLE_MS = 150;
let settleTimer: ReturnType<typeof setTimeout> | undefined;

const liveView = (): ZoomView => ({ zoom: zoom.value, tx: tx.value, ty: ty.value });
const zoomStyle = computed(() => zoomBoxStyle(liveView(), committed.value));

function setView(v: ZoomView): void {
  zoom.value = v.zoom;
  tx.value = v.tx;
  ty.value = v.ty;
}

function settle(): void {
  clearTimeout(settleTimer);
  committed.value = liveView();
}

function settleSoon(): void {
  clearTimeout(settleTimer);
  settleTimer = setTimeout(settle, SETTLE_MS);
}

onBeforeUnmount(() => clearTimeout(settleTimer));

function switchMode(next: 'side' | 'lens'): void {
  mode.value = next;
  resetZoom();
}

function resetZoom(): void {
  setView(IDENTITY_VIEW);
  settle();
}

function containerPoint(
  el: HTMLElement,
  e: PointerEvent | WheelEvent | MouseEvent
): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return {
    x: Math.min(Math.max(e.clientX - r.left, 0), r.width),
    y: Math.min(Math.max(e.clientY - r.top, 0), r.height),
  };
}

function onWheel(e: WheelEvent): void {
  const el = e.currentTarget as HTMLElement;
  const p = containerPoint(el, e);
  const live = liveView();
  // Keeps the content point under the cursor stationary while scaling.
  const next = zoomAt(live, p, e.deltaY < 0 ? 1.18 : 1 / 1.18);
  if (next === live) return;
  setView(next);
  settleSoon();
}

function onPointerDown(e: PointerEvent): void {
  if (zoom.value <= 1) return;
  dragging.value = true;
  dragStart = { x: e.clientX, y: e.clientY, tx: tx.value, ty: ty.value };
  try {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  } catch {
    /* capture is best effort */
  }
}

const ringVisible = ref(false);
const ring = reactive({ x: 0, y: 0 });
const ringStyle = computed(() => ({ left: `${ring.x}px`, top: `${ring.y}px` }));

function onPointerMove(e: PointerEvent): void {
  const el = e.currentTarget as HTMLElement;
  const p = containerPoint(el, e);
  ringVisible.value = true;
  ring.x = p.x;
  ring.y = p.y;
  if (dragging.value && dragStart) {
    tx.value = dragStart.tx + (e.clientX - dragStart.x);
    ty.value = dragStart.ty + (e.clientY - dragStart.y);
  }
}

function onPointerUp(): void {
  if (dragging.value) settle();
  dragging.value = false;
  dragStart = null;
}

const lens = reactive({ x: '50%', y: '50%' });
const lensVars = computed(() => ({ '--mx': lens.x, '--my': lens.y, '--lens-r': '64px' }));

function onLensMove(e: PointerEvent): void {
  const el = e.currentTarget as HTMLElement;
  const p = containerPoint(el, e);
  lens.x = `${p.x}px`;
  lens.y = `${p.y}px`;
  if (dragging.value && dragStart) {
    tx.value = dragStart.tx + (e.clientX - dragStart.x);
    ty.value = dragStart.ty + (e.clientY - dragStart.y);
  }
}

watch(selectedIndex, resetZoom);
</script>

<style scoped>
.checker {
  background: repeating-conic-gradient(#f0f0f0 0% 25%, #ffffff 0% 50%) 0 0 / 20px 20px;
}
.pane,
.compare {
  position: relative;
  aspect-ratio: 1 / 1;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  overflow: hidden;
  touch-action: none;
}
.compare {
  width: 100%;
  max-height: 460px;
}
/* Laid out at the committed zoom (left/top/width/height from zoomBoxStyle);
   the transform only covers a wheel gesture still in progress. */
.zoomable {
  position: absolute;
  transform-origin: 0 0;
  will-change: transform;
}
:deep(.viewer-img),
:deep(.viewer-embed) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
:deep(.viewer-wrap) {
  position: absolute;
  inset: 0;
}
:deep(.viewer-img--pending) {
  opacity: 0;
}
:deep(.pane-message--overlay) {
  pointer-events: none;
}
:deep(.viewer-img) {
  object-fit: contain;
  /* Scales with the laid-out zoom so zooming stays linear (compareZoom.ts). */
  padding: calc(14px * var(--zoom, 1));
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
:deep(.viewer-embed) {
  border: 0;
}
:deep(.pane-message) {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: #666;
}
:deep(.pane-message--actions) {
  pointer-events: auto;
}
:deep(.cta-button) {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid #1d2f5f;
  background: #fff;
  color: #1d2f5f;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
:deep(.cta-button:disabled) {
  opacity: 0.5;
  cursor: default;
}
:deep(.ext-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #1d2f5f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}
.ext-badge {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #1d2f5f;
}
.layer-tag {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(26, 26, 26, 0.72);
  color: #fff;
  pointer-events: none;
  z-index: 2;
  white-space: nowrap;
}
.tag-ai {
  background: rgba(43, 65, 112, 0.85);
}
.tag-manual {
  background: rgba(11, 122, 116, 0.9);
}
.tag-none {
  background: rgba(120, 120, 120, 0.8);
}
.cursor-ring {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 2px solid rgba(80, 80, 80, 0.75);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
}
/* The lens clip sits OUTSIDE the zoom transform so its circle stays in
   container coordinates under the cursor at any zoom level. */
.vector-clip {
  position: absolute;
  inset: 0;
  clip-path: circle(var(--lens-r) at var(--mx) var(--my));
}
.lens-ring {
  position: absolute;
  left: var(--mx);
  top: var(--my);
  width: calc(var(--lens-r) * 2);
  height: calc(var(--lens-r) * 2);
  transform: translate(-50%, -50%);
  border: 2px solid #17d1c6;
  border-radius: 50%;
  box-shadow:
    0 0 14px rgba(23, 209, 198, 0.65),
    inset 0 0 10px rgba(23, 209, 198, 0.35);
  pointer-events: none;
}
.compare .tag-original {
  top: 10px;
  left: 10px;
  transform: none;
}
.compare .tag-vectorized {
  left: var(--mx);
  top: calc(var(--my) - var(--lens-r) - 18px);
  transform: translateX(-50%);
}
</style>
